import { useCallback, useEffect, useRef, useState } from 'react';
import auth from '../api/bank-account/auth';
import getAuthMethods from '../api/bank/getAuthMethods';
import {
    getBankAccountAuth,
    isAuthLoginComplete,
    isChallengeExpired,
    isPushPending,
    parseAuthMethodsResponse,
    shouldContinuePushWait,
} from '../helpers/bankAccountAuth';

const PUSH_WAIT_MS = 2 * 60 * 1000;
const PUSH_POLL_INTERVAL_MS = 1500;

export default function useBankAccountAuthFlow({ accessToken, open, onSuccess }) {
    const [step, setStep] = useState('bank');
    const [bankName, setBankName] = useState('');
    const [bankIcon, setBankIcon] = useState('');
    const [authMethods, setAuthMethods] = useState([]);
    const [authMethod, setAuthMethod] = useState('CHECK_USERNAME');
    const [authNote, setAuthNote] = useState('');
    const [username, setUsername] = useState('');
    const [params, setParams] = useState({});
    const [otpMode, setOtpMode] = useState(null);
    const [qrCode, setQrCode] = useState('');
    const [authMessage, setAuthMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pushWaiting, setPushWaiting] = useState(false);
    const [pushSecondsLeft, setPushSecondsLeft] = useState(0);

    const pollTimerRef = useRef(null);
    const pushDeadlineRef = useRef(0);
    const countdownTimerRef = useRef(null);
    const pushStartedRef = useRef(false);
    const authMethodRef = useRef(authMethod);
    const paramsRef = useRef(params);
    const usernameRef = useRef(username);
    const bankNameRef = useRef(bankName);

    authMethodRef.current = authMethod;
    paramsRef.current = params;
    usernameRef.current = username;
    bankNameRef.current = bankName;

    const clearPollTimer = useCallback(() => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }, []);

    const clearCountdownTimer = useCallback(() => {
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
    }, []);

    const resetFlow = useCallback(() => {
        clearPollTimer();
        clearCountdownTimer();
        setStep('bank');
        setBankName('');
        setBankIcon('');
        setAuthMethods([]);
        setAuthMethod('CHECK_USERNAME');
        setAuthNote('');
        setUsername('');
        setParams({});
        setOtpMode(null);
        setQrCode('');
        setAuthMessage('');
        setLoading(false);
        setError('');
        setPushWaiting(false);
        setPushSecondsLeft(0);
        pushDeadlineRef.current = 0;
        pushStartedRef.current = false;
    }, [clearCountdownTimer, clearPollTimer]);

    useEffect(() => {
        if (!open) {
            resetFlow();
        }
    }, [open, resetFlow]);

    useEffect(() => () => {
        clearPollTimer();
        clearCountdownTimer();
    }, [clearCountdownTimer, clearPollTimer]);

    const applyAuthResponse = useCallback((response) => {
        const authData = getBankAccountAuth(response);

        if (authData.authMessage) {
            setAuthMessage(authData.authMessage);
        }
        if (authData.qrCode) {
            setQrCode(authData.qrCode);
        }
        if (authData.otpMode) {
            setOtpMode(authData.otpMode);
        }

        if (response.success && isAuthLoginComplete(response)) {
            clearPollTimer();
            clearCountdownTimer();
            setPushWaiting(false);
            setStep('success');
            onSuccess?.();
            return { done: true, response };
        }

        if (isChallengeExpired(response)) {
            clearPollTimer();
            clearCountdownTimer();
            setPushWaiting(false);
            const nextStep = authData.nextStep || 'LOGIN_WITH_PASSWORD';
            setAuthMethod(nextStep);
            setError(authData.authMessage || response.message || 'Giao dịch đã hết hiệu lực. Vui lòng thử lại.');
            return { done: false, response, challengeExpired: true };
        }

        if (response.success) {
            setParams(extractAuthPayloadData(response));
            if (authData.qrRefreshed) {
                pushStartedRef.current = false;
            }
            if (authData.nextStep) {
                setAuthMethod(authData.nextStep);
            } else {
                const methods = authMethods.length ? authMethods : ['CHECK_USERNAME', 'LOGIN_WITH_PASSWORD', 'LOGIN_WITH_OTP'];
                const currentIndex = methods.indexOf(authMethodRef.current);
                if (currentIndex >= 0 && currentIndex < methods.length - 1) {
                    setAuthMethod(methods[currentIndex + 1]);
                }
            }
            setError('');
            return { done: false, response, continuePush: shouldContinuePushWait(response) };
        }

        setError(response.message || authData.authMessage || 'Xác thực thất bại');
        return { done: false, response };
    }, [authMethods, clearCountdownTimer, clearPollTimer, onSuccess]);

    const extractAuthPayloadData = (response) => {
        const data = response.data && typeof response.data === 'object' ? response.data : response;
        return data;
    };

    const startPushCountdown = useCallback(() => {
        pushDeadlineRef.current = Date.now() + PUSH_WAIT_MS;
        clearCountdownTimer();
        const tick = () => {
            const left = Math.max(0, Math.ceil((pushDeadlineRef.current - Date.now()) / 1000));
            setPushSecondsLeft(left);
            if (left <= 0) {
                clearCountdownTimer();
                setPushWaiting(false);
                setError('Hết thời gian chờ xác nhận trên app (2 phút).');
            }
        };
        tick();
        countdownTimerRef.current = setInterval(tick, 1000);
    }, [clearCountdownTimer]);

    const submitAuth = useCallback(async ({ cmd, password = '', otp = '', username: usernameOverride }) => {
        setLoading(true);
        setError('');
        const currentUsername = usernameOverride ?? usernameRef.current;
        if (usernameOverride) {
            setUsername(usernameOverride);
        }
        const response = await auth(accessToken, {
            bankName: bankNameRef.current,
            username: currentUsername,
            cmd,
            password,
            otp,
            data: paramsRef.current,
        });
        const result = applyAuthResponse(response);
        setLoading(false);
        return { response, ...result };
    }, [accessToken, applyAuthResponse]);

    const pollPushConfirm = useCallback(async (auto = false) => {
        if (pushDeadlineRef.current && Date.now() >= pushDeadlineRef.current) {
            if (auto) {
                setPushWaiting(false);
                setError('Hết thời gian chờ xác nhận trên app (2 phút).');
                return;
            }
            startPushCountdown();
        } else if (!pushDeadlineRef.current) {
            startPushCountdown();
        }

        setPushWaiting(true);
        const { response, done, continuePush } = await submitAuth({ cmd: 'LOGIN_WITH_OTP', otp: '' });

        if (done) {
            return;
        }

        if (continuePush || shouldContinuePushWait(response) || isPushPending(response)) {
            clearPollTimer();
            pollTimerRef.current = setTimeout(() => {
                pollPushConfirm(true);
            }, PUSH_POLL_INTERVAL_MS);
            return;
        }

        if (!auto) {
            setPushWaiting(false);
            setError(getBankAccountAuth(response).authMessage || response.message || 'Xác thực thất bại');
        }
    }, [clearPollTimer, startPushCountdown, submitAuth]);

    const beginPushFlow = useCallback(() => {
        if (pushStartedRef.current) {
            return;
        }
        pushStartedRef.current = true;
        startPushCountdown();
        setPushWaiting(true);
        clearPollTimer();
        pollTimerRef.current = setTimeout(() => {
            pollPushConfirm(true);
        }, PUSH_POLL_INTERVAL_MS);
    }, [clearPollTimer, pollPushConfirm, startPushCountdown]);

    const selectBank = useCallback(async (value, icon = '') => {
        setBankName(value);
        setBankIcon(icon);
        setStep('auth');
        setLoading(true);
        const response = await getAuthMethods(accessToken, value);
        if (response.success) {
            const parsed = parseAuthMethodsResponse(response);
            setAuthMethods(parsed.methods);
            setAuthNote(parsed.note || '');
            setAuthMethod(parsed.methods[0] || 'CHECK_USERNAME');
        } else {
            setError('Không tải được phương thức xác thực');
        }
        setLoading(false);
    }, [accessToken]);

    const goBackToBank = useCallback(() => {
        clearPollTimer();
        clearCountdownTimer();
        setStep('bank');
        setAuthMethod('CHECK_USERNAME');
        setParams({});
        setOtpMode(null);
        setQrCode('');
        setAuthMessage('');
        setError('');
        setPushWaiting(false);
    }, [clearCountdownTimer, clearPollTimer]);

    return {
        step,
        bankName,
        bankIcon,
        authMethods,
        authMethod,
        authNote,
        username,
        setUsername,
        otpMode,
        qrCode,
        authMessage,
        loading,
        error,
        pushWaiting,
        pushSecondsLeft,
        selectBank,
        goBackToBank,
        submitAuth,
        beginPushFlow,
        pollPushConfirm,
        resetFlow,
        setAuthMethod,
    };
}
